import { usePostHog } from 'posthog-js/react';
import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import FieldzoneModal from '../components/FieldzoneModal';
import { Environment } from '../constants/environments';
import { EventTrackLabel } from '../constants/ga4';
import { useCartContext } from '../hooks/contexts/cart.context';
import { useEventContext } from '../hooks/contexts/event.context';
import { useI18nContext } from '../hooks/contexts/i18n.context';
import { useLoaderScreenContext } from '../hooks/contexts/loader-screen.context';
import { useModalContext } from '../hooks/contexts/modal.context';
import { useUserContext } from '../hooks/contexts/user.context';
import useGuardian from '../hooks/useGuardian';
import { useMetaConversions } from '../hooks/useMetaConversions';
import * as Styles from '../styles/pages/payment';
import FacebookPixel from '../trackings/FacebookPixel';
import GoogleAnalytics from '../trackings/GoogleAnalytics';
import { Checkout } from '../types/checkout';
import { logger } from '../utils/logger';

const PaymentPage: React.FC = () => {
  const posthog = usePostHog();

  const [iframeUrl, setIframeUrl] = useState<string | null>(null);
  const [checkoutPaid, setCheckoutPaid] = useState(false);

  const { couponApplied } = useEventContext();

  const { render, navigateToNextRoute } = useGuardian();
  const {
    namingData,
    prepareCheckoutData,
    requestCheckoutPaymentUrl,
    summaryData,
    updateTransactionId,
    transactionId,
    combosInNaming,
    prepareCheckoutComboData,
    cart,
    combosInCart,
  } = useCartContext();
  const { user } = useUserContext();
  const { event } = useEventContext();
  const { toggleLoader } = useLoaderScreenContext();
  const location = useLocation();
  const { currentLocale : { locale }, translate } = useI18nContext();
  const { clientUrl } = useParams();
  const { setContentModal, toggleModal } = useModalContext();

  async function requestCheckoutUrl({
    checkoutData,
  }: {
    checkoutData: Checkout;
  }) {
    toggleLoader({ show : true });
    const gatewayDate = await requestCheckoutPaymentUrl(checkoutData);
    updateTransactionId(String(gatewayDate?.transactionId));
    if (!gatewayDate) {
      toggleLoader({ show : false });
      return;
    }

    if (gatewayDate.checkoutUrl === 'approved') {
      toggleLoader({ show : false });
      navigateToNextRoute({ route : 'payment-confirmed' });
      return;
    }

    setIframeUrl(gatewayDate.checkoutUrl);
    logger.info(gatewayDate.checkoutUrl);
    toggleLoader({ show : false });
  }

  const handleAnalyticsTracking = (paymentMethod?: string) => {
    GoogleAnalytics.trackEvent(
      EventTrackLabel.BEGIN_CHECKOUT,
      {
        currency : String(event?.accepted_currencies),
        summaryData,
        event,
        couponApplied,
        value : summaryData.reduce(
          (acc, ticket) => acc + ticket.accumulatedValueInCart,
          0
        ),
        items : summaryData.map((ticket) => ({
          item_id : String(event?.id),
          item_name : String(event?.title),
          coupon : couponApplied ? couponApplied.coupon.name : '',
          item_category : ticket.date,
          item_brand : ticket.group,
          item_variant : ticket.title,
          quantity : ticket.quantity,
          discount : couponApplied ? couponApplied.coupon.value_discount : 0,
          price : ticket.price,
        }))
      },
    );

    if (paymentMethod)
      GoogleAnalytics.trackEvent(EventTrackLabel.ADD_PAYMENT_INFO,
        {
          currency : String(event?.accepted_currencies),
          payment_type : paymentMethod,
          summaryData,
          event,
          couponApplied,
          value : summaryData.reduce(
            (acc, ticket) => acc + ticket.accumulatedValueInCart,
            0
          ),
          items : summaryData.map((ticket) => ({
            item_id : String(event?.id),
            item_name : String(event?.title),
            coupon : couponApplied ? couponApplied.coupon.name : '',
            item_category : ticket.date,
            item_brand : ticket.group,
            item_variant : ticket.title,
            quantity : ticket.quantity,
            discount : couponApplied ? couponApplied.coupon.value_discount : 0,
            price : ticket.price,
          }))
        });
  };

  const handleAnalyticsTrackingCheckoutPaid = () => {
    // Prevent analytics tracking if summaryData is empty
    if (!summaryData || summaryData.length === 0) {
      logger.warn('Cannot track analytics: summaryData is empty');
      return;
    }

    try {
      posthog.capture('purchase:success_page_view');
      const contentsToFbPixelTrack = summaryData.map((ticket) => ({
        id : String(ticket.id),
        quantity : ticket.quantity,
      }));

      const valueToFbPixelTrackWithTax = summaryData.reduce(
        (acc, ticket) => acc + (ticket.price + ticket.tax) * ticket.quantity,
        0
      );

      FacebookPixel.trackPurchase(
        valueToFbPixelTrackWithTax,
        contentsToFbPixelTrack,
        String(event?.id),
        String(event?.accepted_currencies)
      );

      const totalTax = summaryData.reduce((acc, ticket) => acc + ticket.tax, 0);
      GoogleAnalytics.trackEvent(EventTrackLabel.PURCHASE, {
        currency : String(event?.accepted_currencies),
        affiliation : String(clientUrl),
        transaction_id : transactionId,
        tax : totalTax,
        shipping : 0,
        items : summaryData.map((ticket) => ({
          item_id : String(event?.id),
          item_name : String(event?.title),
          coupon : couponApplied ? couponApplied.coupon.name : '',
          item_category : ticket.date,
          item_brand : ticket.group,
          item_variant : ticket.title,
          quantity : ticket.quantity,
          discount : couponApplied ? couponApplied.coupon.value_discount : 0,
          price : ticket.price,
        })),
        value : summaryData.reduce(
          (acc, ticket) => acc + ticket.accumulatedValueInCart,
          0
        ),
      });
    } catch (error) {
      logger.error(`Failed to track checkout paid analytics: ${error}`);
    }
  };

  const showFieldzoneModal = () => {
    setContentModal(<FieldzoneModal />);
    toggleModal({ open : true });
  };

  // Show Fielzone modal for events with "Fielzone" category
  const handleFielzoneModal = () => {
    const hasFieldzoneCategory = event?.categories?.some(
      (category) => category.name?.toLowerCase().includes('fielzone')
    );

    if (event && hasFieldzoneCategory) {
      showFieldzoneModal();
    }
  };

  // Track Meta Conversions API PageView automatically
  useMetaConversions();

  useEffect(() => {
    if (!user || !event) return;

    // Clear transactionId when accessing payment page to allow new transactions
    if (transactionId) {
      updateTransactionId('');
    }

    // Prevent duplicate transactions if cart is empty (likely after successful purchase)
    if (cart.length === 0 && combosInCart.length === 0) {
      logger.warn('Cart is empty, preventing duplicate transaction request. Redirecting to tickets page.');
      navigateToNextRoute({ route : 'tickets' });
      return;
    }

    const data = prepareCheckoutData({ namingData, user, event });
    const comboData =
      prepareCheckoutComboData({
        comboNamingData : combosInNaming,
        user,
        event,
      }) ?? [];
    if (!data) return;

    let definitiveData = data;
    if (comboData) {
      definitiveData = {
        ...definitiveData,
        tickets : [...data.tickets, ...comboData.tickets],
      };
    }

    requestCheckoutUrl({ checkoutData : definitiveData });
  }, [user, event, namingData, combosInNaming, cart, combosInCart]);

  useEffect(() => {
    if (!iframeUrl) return;

    const checkout = window.IngresseCheckout;

    checkout?.mount('checkout-frame', iframeUrl, {
      locale : locale,
      onSuccess : (eventDetails) => {
        handleSuccess(eventDetails);
      },
      onTimeout : () => {
        // handleTimeout();
      },
      style : {
        textColorPrimary : '212B36',
        textColorSecondary : '454F5B',
        textColorHighlight : '2280EF',
        textColorAccent : 'FF271A',

        bgColorPrimary : 'F4F6F8',
        bgColorSecondary : 'F9FAFB',
        bgColorAccent : 'FF271A14',

        borderColorPrimary : 'C4CDD5',
        borderColorAccent : 'C81F14',

        buttonColorPrimary : 'FF271A',
        buttonColorHover : 'C81F14',

        font : 'Elza',
      },
    });
  }, [iframeUrl]);

  useEffect(() => {
    return () => {
      if (window.IngresseCheckout) {
        window.IngresseCheckout.cancel();
      }
    };
  }, []);

  const handleSuccess = ({ status, type }: IPaymentDetails) => {
    handleAnalyticsTracking(type);

    if (status === 'pending') {
      navigateToNextRoute({ route : 'payment-in-process' });
      return;
    }

    if (status === 'paid' || status === 'authorized' || status === 'partially paid') {
      setCheckoutPaid(true);
      handleAnalyticsTrackingCheckoutPaid();
      if (status === 'paid') {
        handleFielzoneModal();
      }
      return;
    }

    GoogleAnalytics.trackTransactionCancelled(EventTrackLabel.CANCELED_TRANSACTION, {
      event_category : `Embedstore - Etapa ${location.pathname}`,
      event_label : `Usuário: ${String(user?.id)}. Transação ${transactionId}`,
    });
  };

  if (!iframeUrl) return null;

  return render(
    <>
      <Styles.CheckoutContainer id="checkout-frame"></Styles.CheckoutContainer>
      {checkoutPaid && (
        <>
          <Styles.PaymentFooter>
            <Styles.CustomBigButtonOutlined
              id='show-events'
              type='button'
              onClick={() => {
                posthog.capture('purchase:success_page_show_events_click');
                window.location.href = Environment.SITE_URL;
              }}
            >
              {translate(['INFORMATION', 'SEE_MORE_EVENTS'])}
            </Styles.CustomBigButtonOutlined>
            <Styles.CustomBigButton
              id='show-wallet'
              type='button'
              onClick={() => {
                posthog.capture('purchase:success_page_show_wallet_click');
                window.location.href = Environment.WALLET_URL;
              }}
            >
              {translate(['PAYMENT_STATUS.TEXT', 'PT_50'])}
            </Styles.CustomBigButton>
          </Styles.PaymentFooter>
        </>
      )}
    </>
  );
};

export default PaymentPage;
